import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useWishlist } from "../lib/wishlist";
import { useSEO } from "../lib/seo";
import VinylCard from "../components/VinylCard";

export default function Favoritos() {
  const { t } = useI18n();
  const { ids, clear } = useWishlist();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: `${t("wishlist.title")} · Maggie Records`,
    description: t("wishlist.lead"),
  });

  useEffect(() => {
    if (ids.length === 0) {
      setItems([]); setLoading(false); return;
    }
    setLoading(true);
    // fetch each listing by listing_id via catalog filter (we'll do parallel slug-less lookup)
    // simplest: fetch latest 200 and filter (catalog endpoint paginates) — better: dedicated endpoint
    // We use the listings endpoint with per_page=200 and intersect locally.
    api.get("/listings", { params: { per_page: 200 } })
      .then((r) => {
        const idSet = new Set(ids);
        const found = (r.data.items || []).filter((it) => idSet.has(it.listing_id));
        // preserve user-order
        const orderMap = new Map(ids.map((id, i) => [id, i]));
        found.sort((a, b) => orderMap.get(a.listing_id) - orderMap.get(b.listing_id));
        setItems(found);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [ids]);

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-10 md:pt-14" data-testid="wishlist-page">
      <div className="flex items-end justify-between border-b border-border pb-5 mb-8 md:mb-10">
        <div>
          <h1 className="text-2xl md:text-3xl font-medium leading-tight">
            {t("wishlist.title")}
          </h1>
          <p className="text-xs text-muted-foreground mt-1.5">
            {items.length} {items.length === 1 ? t("wishlist.one") : t("wishlist.many")}
          </p>
        </div>
        {ids.length > 0 && (
          <button
            onClick={clear}
            className="text-xs text-muted-foreground hover:text-foreground"
            data-testid="wishlist-clear"
          >
            {t("wishlist.clear")}
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-8 md:gap-x-5 md:gap-y-10">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="aspect-square bg-[hsl(var(--muted))] animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="py-24 text-center border border-dashed border-border" data-testid="wishlist-empty">
          <p className="text-sm text-muted-foreground mb-4">{t("wishlist.empty")}</p>
          <Link to="/catalogo" className="vmr-link-underline text-sm">
            {t("wishlist.browse")} →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-8 md:gap-x-5 md:gap-y-10" data-testid="wishlist-grid">
          {items.map((it) => (
            <VinylCard key={it.listing_id} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}
